import useBillingNotificationEntity from 'hooks/useBillingNotificationEntity';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import {
  faCalendarAlt,
  faCheckCircle,
  faExclamationTriangle,
  faHandHoldingHeart,
  faSearch,
  faStopCircle,
  faUsers,
  faUserTie
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Modal,
  Nav,
  Spinner,
  Table
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import {
  BillingHeader,
  BillingEmptyRow
} from './components/OrganizationBillingLayout';
import {
  formatBillingDate,
  formatUsdCents
} from 'util/organizationBillingFormat';
import {
  useGetOrganizationSponsorshipCandidatesQuery,
  useGetOrganizationSponsorshipsQuery,
  usePreviewOrganizationSponsorshipMutation,
  useStartOrganizationSponsorshipMutation,
  usePreviewOrganizationGroupSponsorshipMutation,
  useStartOrganizationGroupSponsorshipMutation,
  useEndOrganizationSponsorshipMutation
} from 'rtk/org-admin/organizationBilling.slice';

const dataOf = value => value?.Data ?? value?.data ?? value;
const field = (value, name) =>
  value?.[name] ?? value?.[name[0].toLowerCase() + name.slice(1)];
const errorText = error => {
  const value =
    error?.data?.Error?.Data ??
    error?.data?.error?.data ??
    error?.Error?.Data ??
    error?.error?.data ??
    error?.data?.Data ??
    error?.data?.data ??
    error?.Data ??
    error?.Message ??
    error?.message ??
    error?.Text ??
    error?.error;
  return typeof value === 'string'
    ? value
    : 'The request could not be completed.';
};
const requestKey = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const volumeRateApplies = (qualifies, quantity) =>
  Boolean(qualifies) && Number(quantity) >= 2;
const rateLabel = (qualifies, quantity) =>
  volumeRateApplies(qualifies, quantity) ? 'Volume rate' : 'Standard rate';
const currentSponsorship = (candidate, sponsorships) => {
  const attached = field(candidate, 'Sponsorship');
  if (attached && !['Ended', 'Cancelled'].includes(field(attached, 'State'))) {
    return attached;
  }
  const notaryGuid = field(candidate, 'NotaryUserMasterGUID');
  return (sponsorships ?? []).find(
    item =>
      field(item, 'NotaryUserMasterGUID') === notaryGuid &&
      !['Ended', 'Cancelled'].includes(field(item, 'State'))
  );
};
const periodLabel = value => {
  if (value === 'Annual' || value === 'Year') return 'Yearly';
  if (value === 'Monthly' || value === 'Month') return 'Monthly';
  return value || '—';
};
const priceNote = (pricePreview, frequency) => {
  const custom = field(pricePreview, 'PriceSource') === 'Custom';
  const cadence = frequency === 'Annual' ? 'annual' : 'monthly';
  const quantity = Number(field(pricePreview, 'QualifyingQuantity')) || 0;
  const sharing = Boolean(field(pricePreview, 'QualifiesForGroupRate'));
  if (volumeRateApplies(sharing, quantity)) {
    return custom
      ? `Organization price for ${quantity} sharing sponsored notaries.`
      : `Lower ${cadence} price for ${quantity} sharing sponsored notaries.`;
  }
  if (sharing) {
    return custom
      ? 'Organization price for one sharing notary. The lower price applies when two or more sponsored notaries are sharing.'
      : `Standard ${cadence} price for one sharing notary. The lower price applies when two or more sponsored notaries are sharing.`;
  }
  return custom
    ? 'Organization price. This notary is not sharing, so the standard price applies.'
    : `Standard ${cadence} price. This notary is not sharing, so the lower price does not apply.`;
};
const statusBg = state =>
  state === 'Active'
    ? 'success'
    : state === 'Pending'
    ? 'info'
    : state === 'Ending'
    ? 'warning'
    : ['Ended', 'Cancelled', 'Failed'].includes(state)
    ? 'danger'
    : 'secondary';
const notSharingReason = reason =>
  ({
    'sharing-disabled': 'Sharing is turned off.',
    'connection-disconnected': 'The notary is disconnected.',
    'no-hosted-data': 'The notary has not shared any data yet.'
  }[reason] || 'The notary is not sharing data.');
const SharingStatus = ({ sharing, reason }) => (
  <span
    title={
      sharing
        ? 'Shares data, so this notary counts toward the group rate.'
        : `${notSharingReason(reason)} Standard rate applies.`
    }
  >
    <SoftBadge bg={sharing ? 'success' : 'warning'} pill>
      {sharing ? 'Sharing' : 'Not sharing'}
    </SoftBadge>
  </span>
);
SharingStatus.propTypes = {
  sharing: PropTypes.bool,
  reason: PropTypes.string
};
const SponsorshipStatus = ({ state }) => (
  <SoftBadge bg={statusBg(state)} pill>
    {state || 'Sponsored'}
  </SoftBadge>
);
SponsorshipStatus.propTypes = { state: PropTypes.string };
const DEFAULT_PAGE_SIZE = 10;
const isHistorical = item =>
  ['Ended', 'Cancelled'].includes(field(item, 'State'));

const AdminSponsorships = () => {
  const linked = useBillingNotificationEntity('sponsorship');
  const [activeTab, setActiveTab] = useState('current');
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [candidatePage, setCandidatePage] = useState(0);
  const [candidatePageSize, setCandidatePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateTerm, setCandidateTerm] = useState('');
  const [frequency, setFrequency] = useState('Monthly');
  const [selected, setSelected] = useState(null);
  const [pricePreview, setPricePreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const previewSequence = useRef(0);
  const startRequestKey = useRef(null);
  const [ending, setEnding] = useState(null);
  // Notaries ticked for group sponsorship, keyed by GUID so the choice survives paging.
  const [groupSelected, setGroupSelected] = useState({});
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupFrequency, setGroupFrequency] = useState('Monthly');
  const [groupPreview, setGroupPreview] = useState(null);
  const [groupError, setGroupError] = useState(null);
  const groupSequence = useRef(0);
  const groupRequestKey = useRef(null);
  const candidatesQuery = useGetOrganizationSponsorshipCandidatesQuery({
    indexStart: candidatePage * candidatePageSize,
    maxResults: candidatePageSize,
    search: candidateTerm
  });
  const sponsorshipsQuery = useGetOrganizationSponsorshipsQuery({
    indexStart: 0,
    maxResults: 100
  });
  const historyQuery = useGetOrganizationSponsorshipsQuery(
    {
      indexStart: historyPage * historyPageSize,
      maxResults: historyPageSize,
      search: candidateTerm,
      historyOnly: true
    },
    { skip: activeTab !== 'history' || Boolean(linked.entity) }
  );
  const [requestPreview, previewState] =
    usePreviewOrganizationSponsorshipMutation();
  const [confirm, confirmState] = useStartOrganizationSponsorshipMutation();
  const [requestGroupPreview, groupPreviewState] =
    usePreviewOrganizationGroupSponsorshipMutation();
  const [confirmGroup, confirmGroupState] =
    useStartOrganizationGroupSponsorshipMutation();
  const [end, endState] = useEndOrganizationSponsorshipMutation();
  const candidatesPayload = dataOf(candidatesQuery.data);
  const sponsorshipsPayload = dataOf(sponsorshipsQuery.data);
  const candidates = field(candidatesPayload, 'Objects') ?? [];
  // The typed text runs ahead of the debounced query term; count that as loading.
  const candidateLoading =
    candidatesQuery.isFetching || candidateSearch.trim() !== candidateTerm;
  const candidateTotal =
    field(candidatesPayload, 'TotalRecords') ?? candidates.length;
  const sponsorships = linked.entity
    ? [linked.entity]
    : field(sponsorshipsPayload, 'Objects') ?? [];
  // Sponsorships whose notary is not in the connected list still need a row.
  const needle = candidateTerm.toLowerCase();
  const historyPayload = dataOf(historyQuery.data);
  const historyRows =
    linked.entity && isHistorical(linked.entity)
      ? [linked.entity]
      : field(historyPayload, 'Objects') ?? [];
  const historyTotal =
    linked.entity && isHistorical(linked.entity)
      ? 1
      : field(historyPayload, 'TotalRecords') ?? 0;
  const historyLoading =
    historyQuery.isLoading ||
    historyQuery.isFetching ||
    candidateSearch.trim() !== candidateTerm;
  const otherSponsorships = candidatePage
    ? []
    : sponsorships.filter(
        item =>
          !['Ended', 'Cancelled'].includes(field(item, 'State')) &&
          !candidates.some(
            candidate =>
              field(candidate, 'NotaryUserMasterGUID') ===
              field(item, 'NotaryUserMasterGUID')
          ) &&
          (field(item, 'NotaryName') ?? '').toLowerCase().includes(needle)
      );
  const unitAmountCents = field(pricePreview, 'UnitAmountCents');
  const hasPrice = Boolean(
    pricePreview &&
      !field(pricePreview, 'RequiresPricingReview') &&
      unitAmountCents != null &&
      Number.isFinite(Number(unitAmountCents)) &&
      Number(unitAmountCents) >= 0
  );
  const canStart = hasPrice && !previewState.isLoading;
  const startsImmediately = Boolean(field(pricePreview, 'StartsImmediately'));
  const groupGuids = Object.keys(groupSelected);
  const groupItems = field(groupPreview, 'Items') ?? [];
  const groupChargedNow = Number(field(groupPreview, 'ChargedNowCents')) || 0;
  const groupScheduled = Number(field(groupPreview, 'ScheduledCents')) || 0;
  const groupNeedsReview = Boolean(
    field(groupPreview, 'RequiresPricingReview')
  );
  const groupNotSharing = groupItems.filter(
    item => !field(item, 'QualifiesForGroupRate')
  ).length;
  const canStartGroup =
    Boolean(groupPreview) &&
    !groupNeedsReview &&
    groupItems.length >= 2 &&
    !groupPreviewState.isLoading;
  const pageSelectable = candidates.filter(
    candidate => !currentSponsorship(candidate, sponsorships)
  );
  const pageAllSelected =
    pageSelectable.length > 0 &&
    pageSelectable.every(
      candidate => groupSelected[field(candidate, 'NotaryUserMasterGUID')]
    );

  useEffect(() => {
    const timer = setTimeout(() => {
      setCandidateTerm(candidateSearch.trim());
      setCandidatePage(0);
      setHistoryPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [candidateSearch]);

  useEffect(() => {
    if (linked.entity) {
      setActiveTab(isHistorical(linked.entity) ? 'history' : 'current');
    }
  }, [linked.entity]);

  useEffect(() => {
    setHistoryPage(page =>
      Math.min(page, Math.max(0, Math.ceil(historyTotal / historyPageSize) - 1))
    );
  }, [historyTotal, historyPageSize]);

  const openPreview = async (candidate, selectedFrequency) => {
    const sequence = ++previewSequence.current;
    startRequestKey.current = requestKey();
    setSelected(candidate);
    setPricePreview(null);
    setPreviewError(null);
    const requestedFrequency =
      selectedFrequency ?? field(candidate, 'Frequency') ?? frequency;
    setFrequency(requestedFrequency);
    try {
      const result = await requestPreview({
        notaryUserMasterGUID: field(candidate, 'NotaryUserMasterGUID'),
        frequency: requestedFrequency
      }).unwrap();
      if (sequence === previewSequence.current) setPricePreview(dataOf(result));
    } catch (error) {
      if (sequence !== previewSequence.current) return;
      const message = errorText(error);
      setPreviewError(message);
      toast.error(message);
    }
  };

  const startSponsorship = async () => {
    if (!canStart || confirmState.isLoading) return;
    try {
      const result = await confirm({
        notaryUserMasterGUID: field(selected, 'NotaryUserMasterGUID'),
        frequency,
        idempotencyKey: startRequestKey.current ?? requestKey()
      }).unwrap();
      toast.success(
        field(dataOf(result), 'State') === 'Active'
          ? 'Payment received. Sponsorship is active.'
          : startsImmediately
          ? 'Payment is processing. Sponsorship activates once it is confirmed.'
          : 'Sponsorship is scheduled. Your card is charged on the renewal date.'
      );
      setSelected(null);
      setPricePreview(null);
    } catch (error) {
      toast.error(errorText(error));
      setPricePreview(null);
    }
  };

  const toggleGroupMember = candidate => {
    const guid = field(candidate, 'NotaryUserMasterGUID');
    setGroupSelected(current => {
      const next = { ...current };
      if (next[guid]) delete next[guid];
      else next[guid] = candidate;
      return next;
    });
  };

  const togglePageMembers = () => {
    setGroupSelected(current => {
      const next = { ...current };
      pageSelectable.forEach(candidate => {
        const guid = field(candidate, 'NotaryUserMasterGUID');
        if (pageAllSelected) delete next[guid];
        else next[guid] = candidate;
      });
      return next;
    });
  };

  const closeGroup = () => {
    groupSequence.current += 1;
    setGroupOpen(false);
    setGroupPreview(null);
    setGroupError(null);
  };

  const openGroupPreview = async selectedFrequency => {
    const sequence = ++groupSequence.current;
    groupRequestKey.current = requestKey();
    setGroupOpen(true);
    setGroupPreview(null);
    setGroupError(null);
    setGroupFrequency(selectedFrequency);
    try {
      const result = await requestGroupPreview({
        notaryUserMasterGUIDs: groupGuids,
        frequency: selectedFrequency
      }).unwrap();
      if (sequence === groupSequence.current) setGroupPreview(dataOf(result));
    } catch (error) {
      if (sequence !== groupSequence.current) return;
      const message = errorText(error);
      setGroupError(message);
      toast.error(message);
    }
  };

  const startGroupSponsorship = async () => {
    if (!canStartGroup || confirmGroupState.isLoading) return;
    try {
      const result = await confirmGroup({
        notaryUserMasterGUIDs: groupGuids,
        frequency: groupFrequency,
        idempotencyKey: groupRequestKey.current ?? requestKey()
      }).unwrap();
      const items = field(dataOf(result), 'Items') ?? [];
      const failed = items.filter(
        item => field(item, 'State') === 'Cancelled'
      ).length;
      if (failed) {
        toast.warning(
          `${items.length - failed} of ${
            items.length
          } sponsorships started. ${failed} could not be charged and ${
            failed === 1 ? 'was' : 'were'
          } not started.`
        );
      } else {
        toast.success(
          groupChargedNow > 0
            ? 'Group sponsorship confirmed. Payment is being processed.'
            : 'Group sponsorship is scheduled. Your card is charged on each renewal date.'
        );
      }
      setGroupSelected({});
      closeGroup();
    } catch (error) {
      toast.error(errorText(error));
      setGroupPreview(null);
    }
  };

  const endSponsorship = async () => {
    if (!ending) return;
    try {
      await end({
        guid: field(ending, 'GUID'),
        idempotencyKey: requestKey()
      }).unwrap();
      toast.success('Sponsorship end has been scheduled.');
      setEnding(null);
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <Card>
        <BillingHeader
          title="Sponsorships"
          subtitle="Your default card is charged when you confirm if the notary has no active paid subscription, or on the existing subscription's renewal date."
          onRefresh={() => {
            candidatesQuery.refetch();
            sponsorshipsQuery.refetch();
            if (activeTab === 'history' && !linked.entity)
              historyQuery.refetch();
          }}
          loading={
            activeTab === 'history'
              ? historyLoading
              : candidatesQuery.isFetching || sponsorshipsQuery.isFetching
          }
        />
        <Card.Body>
          {linked.targetGuid && (
            <Alert variant={linked.error ? 'danger' : 'info'}>
              {linked.error ||
                (linked.loading
                  ? 'Loading the affected sponsorship...'
                  : 'Showing the affected sponsorship.')}{' '}
              <Link to="/organization/billing/sponsorships">
                Show all sponsorships
              </Link>
            </Alert>
          )}
          <Nav
            variant="tabs"
            className="mb-3"
            role="tablist"
            aria-label="Sponsorship views"
          >
            <Nav.Item>
              <Nav.Link
                as="button"
                type="button"
                role="tab"
                active={activeTab === 'current'}
                aria-selected={activeTab === 'current'}
                onClick={() => setActiveTab('current')}
              >
                Current
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                as="button"
                type="button"
                role="tab"
                active={activeTab === 'history'}
                aria-selected={activeTab === 'history'}
                onClick={() => setActiveTab('history')}
              >
                History
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="text-600 fs--1">
              {activeTab === 'history'
                ? `${historyTotal} past ${
                    historyTotal === 1 ? 'sponsorship' : 'sponsorships'
                  }`
                : `${candidateTotal} connected ${
                    candidateTotal === 1 ? 'notary' : 'notaries'
                  }`}
            </div>
            <InputGroup className="w-auto">
              <Form.Control
                type="search"
                value={candidateSearch}
                onChange={event => setCandidateSearch(event.target.value)}
                placeholder="Search notaries..."
                aria-label={
                  activeTab === 'history'
                    ? 'Search sponsorship history'
                    : 'Search connected notaries'
                }
              />
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
            </InputGroup>
          </div>
          {activeTab === 'current' && (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded-3 bg-light px-3 py-2 mb-3">
              <div className="fs--1 text-700">
                <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                {groupGuids.length
                  ? `${groupGuids.length} ${
                      groupGuids.length === 1 ? 'notary' : 'notaries'
                    } selected${
                      groupGuids.length < 2
                        ? '. Select at least two for group pricing.'
                        : '.'
                    }`
                  : 'Tick two or more notaries to sponsor them together at the group rate.'}
              </div>
              {groupGuids.length > 0 && (
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setGroupSelected({})}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    disabled={groupGuids.length < 2}
                    onClick={() => openGroupPreview(groupFrequency)}
                  >
                    <FontAwesomeIcon
                      icon={faHandHoldingHeart}
                      className="me-1"
                    />
                    Sponsor as group
                  </Button>
                </div>
              )}
            </div>
          )}
          {activeTab === 'current' && sponsorshipsQuery.isError && (
            <Alert variant="danger">{errorText(sponsorshipsQuery.error)}</Alert>
          )}
          {activeTab === 'history' && historyQuery.isError && (
            <Alert variant="danger">{errorText(historyQuery.error)}</Alert>
          )}
          {(activeTab === 'history'
            ? historyQuery.isLoading
            : candidatesQuery.isLoading) && (
            <Spinner animation="border" role="status">
              <span className="visually-hidden">
                {activeTab === 'history'
                  ? 'Loading sponsorship history'
                  : 'Loading notaries'}
              </span>
            </Spinner>
          )}
          {activeTab === 'current' && candidatesQuery.isError && (
            <Alert variant="danger">{errorText(candidatesQuery.error)}</Alert>
          )}
          {activeTab === 'history' ? (
            <div className="mb-2">
              <Pagination
                className="py-0"
                pageIndex={historyPage}
                pageSize={historyPageSize}
                totalRecords={historyTotal}
                currentCount={historyRows.length}
                onPageChange={setHistoryPage}
                onPageSizeChange={size => {
                  setHistoryPageSize(size);
                  setHistoryPage(0);
                }}
                endOfResults={field(historyPayload, 'EndOfResults') ?? true}
                disabled={historyLoading}
                onRefresh={() => {
                  if (!linked.entity) historyQuery.refetch();
                }}
                loading={historyLoading}
                pageSizeOptions={[5, 10, 15, 25]}
              />
            </div>
          ) : (
            !candidatesQuery.isLoading && (
              <div className="mb-2">
                <Pagination
                  className="py-0"
                  pageIndex={candidatePage}
                  pageSize={candidatePageSize}
                  totalRecords={candidateTotal}
                  currentCount={candidates.length}
                  onPageChange={setCandidatePage}
                  onPageSizeChange={size => {
                    setCandidatePageSize(size);
                    setCandidatePage(0);
                  }}
                  endOfResults={
                    field(candidatesPayload, 'EndOfResults') ?? true
                  }
                  disabled={
                    candidatesQuery.isLoading || candidatesQuery.isFetching
                  }
                  onRefresh={() => candidatesQuery.refetch()}
                  loading={candidatesQuery.isFetching}
                  pageSizeOptions={[5, 10, 15, 25]}
                />
              </div>
            )
          )}
          {activeTab === 'history' ? (
            <div className="table-responsive border rounded-3">
              <Table
                hover
                size="sm"
                className={`fs--1 mb-0 align-middle table-borderless${
                  historyLoading ? ' refreshing-skeleton' : ''
                }`}
              >
                <thead className="bg-200 text-900 align-middle">
                  <tr>
                    <th>Notary</th>
                    <th>Status</th>
                    <th>Billing period</th>
                    <th>Started</th>
                    <th>Ended</th>
                  </tr>
                </thead>
                <tbody>
                  {!historyLoading &&
                    !historyQuery.isError &&
                    !historyRows.length && (
                      <BillingEmptyRow colSpan={5}>
                        {candidateTerm
                          ? 'No past sponsorships match this search.'
                          : 'No ended or cancelled sponsorships yet.'}
                      </BillingEmptyRow>
                    )}
                  {historyRows.map(item => (
                    <tr
                      key={field(item, 'GUID')}
                      className={
                        linked.targetGuid === field(item, 'GUID')
                          ? 'table-info'
                          : undefined
                      }
                    >
                      <td className="fw-semi-bold text-900">
                        {field(item, 'NotaryName') || 'Notary name unavailable'}
                      </td>
                      <td>
                        <SponsorshipStatus state={field(item, 'State')} />
                      </td>
                      <td className="text-600">
                        {periodLabel(field(item, 'Frequency'))}
                      </td>
                      <td className="text-nowrap">
                        {field(item, 'State') === 'Cancelled'
                          ? 'Never started'
                          : formatBillingDate(field(item, 'EffectiveFromUtc'))}
                      </td>
                      <td className="text-nowrap">
                        {field(item, 'State') === 'Cancelled'
                          ? 'Before start'
                          : formatBillingDate(field(item, 'EffectiveUntilUtc'))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="table-responsive border rounded-3">
              <Table
                hover
                size="sm"
                className={`fs--1 mb-0 align-middle table-borderless${
                  candidateLoading ? ' refreshing-skeleton' : ''
                }`}
                style={{ width: '100%', wordBreak: 'normal' }}
              >
                <thead className="bg-200 text-900 align-middle">
                  <tr>
                    <th style={{ width: 32 }}>
                      <Form.Check
                        type="checkbox"
                        aria-label="Select all notaries on this page"
                        checked={pageAllSelected}
                        disabled={!pageSelectable.length}
                        onChange={togglePageMembers}
                      />
                    </th>
                    <th>Notary</th>
                    <th>Status</th>
                    <th>Sharing</th>
                    <th>Billing period</th>
                    <th>Organization pays from</th>
                    <th>Ends</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!candidatesQuery.isLoading &&
                    !candidatesQuery.isError &&
                    !candidates.length &&
                    !otherSponsorships.length && (
                      <BillingEmptyRow colSpan={8}>
                        {candidateTerm
                          ? 'No notaries match this search.'
                          : 'No connected notaries are available.'}
                      </BillingEmptyRow>
                    )}{' '}
                  {otherSponsorships.map(item => (
                    <tr
                      key={field(item, 'GUID')}
                      className={
                        linked.targetGuid === field(item, 'GUID')
                          ? 'table-info'
                          : undefined
                      }
                    >
                      <td />
                      <td className="fw-semi-bold text-900">
                        {field(item, 'NotaryName') || 'Notary name unavailable'}
                      </td>
                      <td>
                        <SponsorshipStatus state={field(item, 'State')} />
                      </td>
                      <td className="text-600">—</td>
                      <td className="text-600">
                        {periodLabel(field(item, 'Frequency'))}
                      </td>
                      <td className="text-nowrap">
                        {formatBillingDate(field(item, 'EffectiveFromUtc'))}
                      </td>
                      <td className="text-nowrap">
                        {formatBillingDate(field(item, 'EffectiveUntilUtc'))}
                      </td>
                      <td className="text-nowrap text-end">
                        {['Pending', 'Active'].includes(
                          field(item, 'State')
                        ) ? (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setEnding(item)}
                          >
                            <FontAwesomeIcon
                              icon={faStopCircle}
                              className="me-1"
                            />
                            End
                          </Button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                  {candidates.map(candidate => {
                    const sponsorship = currentSponsorship(
                      candidate,
                      sponsorships
                    );
                    return (
                      <tr
                        key={field(candidate, 'NotaryUserMasterGUID')}
                        className={
                          sponsorship &&
                          linked.targetGuid === field(sponsorship, 'GUID')
                            ? 'table-info'
                            : undefined
                        }
                      >
                        <td>
                          {!sponsorship && (
                            <Form.Check
                              type="checkbox"
                              aria-label={`Select ${
                                field(candidate, 'NotaryName') || 'notary'
                              } for group sponsorship`}
                              checked={Boolean(
                                groupSelected[
                                  field(candidate, 'NotaryUserMasterGUID')
                                ]
                              )}
                              onChange={() => toggleGroupMember(candidate)}
                            />
                          )}
                        </td>
                        <td className="fw-semi-bold text-900">
                          {field(candidate, 'NotaryName') ||
                            'Notary name unavailable'}
                        </td>
                        <td>
                          {sponsorship ? (
                            <SponsorshipStatus
                              state={field(sponsorship, 'State')}
                            />
                          ) : (
                            <SoftBadge bg="warning" pill>
                              Not sponsored
                            </SoftBadge>
                          )}
                        </td>
                        <td>
                          <SharingStatus
                            sharing={Boolean(
                              field(candidate, 'QualifiesForGroupRate')
                            )}
                            reason={field(candidate, 'EligibilityReason')}
                          />
                        </td>
                        <td className="text-600">
                          {sponsorship
                            ? periodLabel(field(sponsorship, 'Frequency'))
                            : '—'}
                        </td>
                        <td className="text-nowrap">
                          {sponsorship
                            ? formatBillingDate(
                                field(sponsorship, 'EffectiveFromUtc')
                              )
                            : field(candidate, 'NextRenewalUtc')
                            ? formatBillingDate(
                                field(candidate, 'NextRenewalUtc')
                              )
                            : 'Charged on confirmation'}
                        </td>
                        <td className="text-nowrap">
                          {sponsorship
                            ? formatBillingDate(
                                field(sponsorship, 'EffectiveUntilUtc')
                              )
                            : '—'}
                        </td>
                        <td className="text-nowrap text-end">
                          {!sponsorship ? (
                            <Button
                              size="sm"
                              onClick={() => openPreview(candidate)}
                            >
                              <FontAwesomeIcon
                                icon={faHandHoldingHeart}
                                className="me-1"
                              />
                              Sponsor
                            </Button>
                          ) : ['Pending', 'Active'].includes(
                              field(sponsorship, 'State')
                            ) ? (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => setEnding(sponsorship)}
                            >
                              <FontAwesomeIcon
                                icon={faStopCircle}
                                className="me-1"
                              />
                              End
                            </Button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      <Modal
        show={!!selected}
        onHide={() => {
          setSelected(null);
          setPricePreview(null);
          setPreviewError(null);
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton className="px-4 py-3">
          <div className="d-flex align-items-center">
            <div
              className="bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: 42, height: 42 }}
            >
              <FontAwesomeIcon icon={faUserTie} />
            </div>
            <div>
              <Modal.Title className="fs-2">Sponsor subscription</Modal.Title>
              <div className="text-500 fs--1 mt-1">
                Review the price before confirming.
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          <div className="border rounded-3 bg-light p-3 mb-3">
            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <div className="text-500 fs--2 text-uppercase fw-semi-bold mb-1">
                  Notary
                </div>
                <div className="fw-semi-bold text-900">
                  {field(selected, 'NotaryName') ||
                    field(selected, 'NotaryUserMasterGUID')}
                </div>
              </div>
              <Badge
                bg={
                  volumeRateApplies(
                    field(pricePreview, 'QualifiesForGroupRate'),
                    field(pricePreview, 'QualifyingQuantity')
                  )
                    ? 'success'
                    : 'secondary'
                }
                className="fw-normal"
              >
                {rateLabel(
                  field(pricePreview, 'QualifiesForGroupRate'),
                  field(pricePreview, 'QualifyingQuantity')
                )}
              </Badge>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semi-bold mb-2">
              Billing period
            </Form.Label>
            <Form.Select
              value={frequency}
              onChange={event => openPreview(selected, event.target.value)}
              aria-label="Organization sponsorship billing period"
            >
              <option value="Monthly">Monthly</option>
              <option value="Annual">Annual</option>
            </Form.Select>
            <Form.Text className="text-500">
              {startsImmediately
                ? 'A new organization-paid subscription starts when you confirm, and your default card is charged now.'
                : "Your selection takes effect at the notary's next renewal, when your default card is charged."}
            </Form.Text>
          </Form.Group>

          {previewState.isLoading && (
            <div className="border rounded-3 text-center py-4">
              <Spinner animation="border" size="sm" role="status" />
              <div className="text-600 fs--1 mt-2">Calculating price…</div>
            </div>
          )}
          {previewError && (
            <Alert
              variant={
                String(previewError).includes('already sponsored')
                  ? 'warning'
                  : 'danger'
              }
              className="mb-0 d-flex align-items-start"
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mt-1 me-2"
              />
              <div>
                <div className="fw-semi-bold">
                  {String(previewError).includes('already sponsored')
                    ? 'Notary already sponsored'
                    : 'Unable to prepare price preview'}
                </div>
                <div className="fs--1 mt-1">{previewError}</div>
              </div>
            </Alert>
          )}
          {pricePreview && field(pricePreview, 'RequiresPricingReview') && (
            <Alert variant="warning" className="mb-0 d-flex align-items-start">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mt-1 me-3"
              />
              <div>
                <div className="fw-bold text-900">Pricing review required</div>
                <div className="fs--1 mt-1">
                  No active {frequency.toLowerCase()} sponsorship price covers
                  this notary. Add a global standard price or an organization
                  custom price, then refresh the price preview.
                </div>
              </div>
            </Alert>
          )}
          {pricePreview && (
            <div className="border rounded-3 overflow-hidden">
              <div className="bg-soft-primary px-3 py-3">
                <div className="text-600 fs--2 text-uppercase fw-semi-bold">
                  Organization rate
                </div>
                <div className="d-flex align-items-baseline mt-1">
                  <span className="fs-2 fw-bold text-900">
                    {hasPrice
                      ? formatUsdCents(unitAmountCents)
                      : 'Pricing unavailable'}
                  </span>
                  <span className="text-600 fs--1 ms-1">
                    / {frequency === 'Annual' ? 'year' : 'month'}
                  </span>
                </div>
                <div className="text-600 fs--1 mt-1">
                  {hasPrice && priceNote(pricePreview, frequency)}
                </div>
                {hasPrice && frequency === 'Annual' && (
                  <div className="text-600 fs--1 mt-1">
                    {formatUsdCents(Number(unitAmountCents) / 12)} per month,
                    billed annually.
                  </div>
                )}
              </div>
              <div className="px-3 py-2">
                <div className="d-flex align-items-center py-2 border-bottom">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-primary me-3"
                  />
                  <div>
                    <div className="text-500 fs--2">Your card is charged</div>
                    <div className="fs--1 fw-semi-bold text-800">
                      {startsImmediately
                        ? 'Now, when you confirm'
                        : formatBillingDate(
                            field(pricePreview, 'EffectiveFromUtc')
                          )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-light border-top px-3 py-2 fs--1 text-600">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-success me-2"
                />
                {startsImmediately
                  ? `Your default card is charged ${
                      hasPrice
                        ? formatUsdCents(unitAmountCents)
                        : 'the price above'
                    } immediately and the notary's ${
                      frequency === 'Annual' ? 'annual' : 'monthly'
                    } billing cycle starts today. Later ${
                      frequency === 'Annual' ? 'years are' : 'months are'
                    } charged ${
                      frequency === 'Annual' ? '14' : '7'
                    } days before renewal. If the charge fails, the sponsorship is not started.`
                  : `The notary keeps paying until that renewal. Nothing is charged today; your default card is charged on the renewal date, then ${
                      frequency === 'Annual' ? '14' : '7'
                    } days before each later renewal. The notary receives each period only after the payment succeeds.`}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="px-4 py-3">
          <Button
            variant="outline-secondary"
            onClick={() => {
              previewSequence.current += 1;
              setSelected(null);
              setPricePreview(null);
              setPreviewError(null);
            }}
          >
            {canStart ? 'Cancel' : 'Close'}
          </Button>
          {canStart && (
            <Button
              disabled={confirmState.isLoading}
              onClick={startSponsorship}
            >
              {confirmState.isLoading
                ? startsImmediately
                  ? 'Charging card…'
                  : 'Confirming…'
                : startsImmediately
                ? hasPrice
                  ? `Confirm and pay ${formatUsdCents(unitAmountCents)}`
                  : 'Confirm and pay'
                : 'Confirm sponsorship'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Modal show={groupOpen} onHide={closeGroup} centered size="lg">
        <Modal.Header closeButton className="px-4 py-3">
          <div className="d-flex align-items-center">
            <div
              className="bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: 42, height: 42 }}
            >
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div>
              <Modal.Title className="fs-2">
                Sponsor {groupGuids.length} notaries
              </Modal.Title>
              <div className="text-500 fs--1 mt-1">
                Group pricing is based on how many selected notaries share data.
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          <Form.Group className="mb-3">
            <Form.Label className="fw-semi-bold mb-2">
              Billing period
            </Form.Label>
            <Form.Select
              value={groupFrequency}
              onChange={event => openGroupPreview(event.target.value)}
              aria-label="Group sponsorship billing period"
            >
              <option value="Monthly">Monthly</option>
              <option value="Annual">Annual</option>
            </Form.Select>
          </Form.Group>
          {groupPreviewState.isLoading && (
            <div className="border rounded-3 text-center py-4">
              <Spinner animation="border" size="sm" role="status" />
              <div className="text-600 fs--1 mt-2">
                Calculating group price…
              </div>
            </div>
          )}
          {groupError && (
            <Alert variant="danger" className="mb-0 d-flex align-items-start">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mt-1 me-2"
              />
              <div>
                <div className="fw-semi-bold">
                  Unable to prepare group price preview
                </div>
                <div className="fs--1 mt-1">{groupError}</div>
              </div>
            </Alert>
          )}
          {groupPreview && groupNeedsReview && (
            <Alert variant="warning" className="d-flex align-items-start">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="mt-1 me-3"
              />
              <div>
                <div className="fw-bold text-900">Pricing review required</div>
                <div className="fs--1 mt-1">
                  No active {groupFrequency.toLowerCase()} sponsorship price
                  covers part of this group. Add a global standard price or an
                  organization custom price, then refresh the price preview.
                </div>
              </div>
            </Alert>
          )}
          {groupPreview && (
            <div className="border rounded-3 overflow-hidden">
              <div className="bg-soft-primary px-3 py-3">
                <div className="text-600 fs--2 text-uppercase fw-semi-bold">
                  Group rate
                </div>
                <div className="text-600 fs--1 mt-1">
                  {Number(field(groupPreview, 'QualifyingQuantity')) >= 2
                    ? `${field(
                        groupPreview,
                        'QualifyingQuantity'
                      )} sharing sponsored notaries qualify for the lower ${
                        groupFrequency === 'Annual' ? 'annual' : 'monthly'
                      } price.`
                    : 'Fewer than two sharing sponsored notaries, so the standard price applies to everyone.'}
                </div>
              </div>
              <Table size="sm" className="fs--1 mb-0 align-middle">
                <thead className="bg-200 text-900">
                  <tr>
                    <th>Notary</th>
                    <th>Rate</th>
                    <th>Card is charged</th>
                    <th className="text-end">
                      Price / {groupFrequency === 'Annual' ? 'year' : 'month'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupItems.map(item => {
                    const guid = field(item, 'NotaryUserMasterGUID');
                    return (
                      <tr key={guid}>
                        <td className="fw-semi-bold text-900">
                          {field(groupSelected[guid], 'NotaryName') || guid}
                        </td>
                        <td>
                          <Badge
                            bg={
                              volumeRateApplies(
                                field(item, 'QualifiesForGroupRate'),
                                field(item, 'QualifyingQuantity')
                              )
                                ? 'success'
                                : 'secondary'
                            }
                            className="fw-normal"
                          >
                            {rateLabel(
                              field(item, 'QualifiesForGroupRate'),
                              field(item, 'QualifyingQuantity')
                            )}
                          </Badge>
                        </td>
                        <td className="text-nowrap">
                          {field(item, 'StartsImmediately')
                            ? 'Now'
                            : formatBillingDate(
                                field(item, 'EffectiveFromUtc')
                              )}
                        </td>
                        <td className="text-end text-nowrap">
                          {field(item, 'RequiresPricingReview')
                            ? 'Unavailable'
                            : formatUsdCents(field(item, 'UnitAmountCents'))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="px-3 py-2 border-top fs--1">
                {groupChargedNow > 0 && (
                  <div className="d-flex justify-content-between fw-semi-bold text-900">
                    <span>Charged now</span>
                    <span>{formatUsdCents(groupChargedNow)}</span>
                  </div>
                )}
                {groupScheduled > 0 && (
                  <div className="d-flex justify-content-between text-700">
                    <span>Charged on each notary's renewal date</span>
                    <span>{formatUsdCents(groupScheduled)}</span>
                  </div>
                )}
              </div>
              <div className="bg-light border-top px-3 py-2 fs--1 text-600">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-success me-2"
                />
                Your default card is charged once, with one invoice, for every
                notary starting now. If that payment fails, none of them start.
                {groupNotSharing > 0 &&
                  ` ${groupNotSharing} ${
                    groupNotSharing === 1 ? 'notary is' : 'notaries are'
                  } not sharing data, so the standard price applies and the lower price does not count ${
                    groupNotSharing === 1 ? 'that notary' : 'them'
                  }.`}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="px-4 py-3">
          <Button variant="outline-secondary" onClick={closeGroup}>
            {canStartGroup ? 'Cancel' : 'Close'}
          </Button>
          {canStartGroup && (
            <Button
              disabled={confirmGroupState.isLoading}
              onClick={startGroupSponsorship}
            >
              {confirmGroupState.isLoading
                ? 'Confirming…'
                : groupChargedNow > 0
                ? `Confirm and pay ${formatUsdCents(groupChargedNow)}`
                : 'Confirm group sponsorship'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Modal show={!!ending} onHide={() => setEnding(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>End sponsorship</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          End sponsorship for{' '}
          {field(ending, 'NotaryName') || field(ending, 'NotaryUserMasterGUID')}
          ? The payer changes at the next subscription boundary.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEnding(null)}>
            Keep sponsorship
          </Button>
          <Button
            variant="danger"
            disabled={endState.isLoading}
            onClick={endSponsorship}
          >
            End sponsorship
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminSponsorships;
